/* eslint-disable react/prop-types */
import type { JSX, MouseEvent, RefAttributes } from 'react';

import type { ButtonProps as ArkButtonProps } from '@remarkable/ark-web';
import {
  // eslint-disable-next-line no-restricted-imports
  Button as ArkButton,
} from '@remarkable/ark-web';
import { Link as RouterLink } from '@tanstack/react-router';

import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { cn } from 'src/utils/classNamesHelper';

type ButtonAsButtonProps = {
  to?: never;
  as?: 'button';
} & JSX.IntrinsicElements['button'] &
  RefAttributes<HTMLButtonElement>;

type ButtonAsLinkProps = {
  to: string;
  as: 'a';
} & JSX.IntrinsicElements['a'] &
  RefAttributes<HTMLAnchorElement>;

type ButtonProps = ArkButtonProps &
  (ButtonAsButtonProps | ButtonAsLinkProps) & {
    disabled?: boolean; // disabled is allowed for both elements, but isn't defined in ArkButtonProps so we have to fix the type here
    mobileFullWidth?: boolean; // custom prop for our Button component
    analytics?: {
      /** Represents the text content of the button. */
      text?: string;
      location: string;
      action: string;
    };
  };

const isLink = (props: ButtonProps): props is ButtonAsLinkProps =>
  props.as === 'a';

export function Button({
  analytics,
  className,
  mobileFullWidth,
  ref,
  ...props
}: ButtonProps) {
  const onClick = (event: unknown) => {
    if (analytics) {
      tracker.trackEvent(
        new ButtonClicked({
          component_location: analytics.location,
          text:
            analytics.text ||
            ((event as MouseEvent<HTMLButtonElement>).target as HTMLElement)
              .innerText,
          action: analytics.action,
        })
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
    props.onClick?.(event as any);
  };

  const newClassName = cn(
    {
      'w-full ls:w-fit': mobileFullWidth,
    },
    className
  );

  if (!isLink(props)) {
    // ArkButton props doesn't infer entirely correct regarding the `as` prop, so we have to cast it to any
    return (
      <ArkButton
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(props as any)}
        ref={ref}
        onClick={onClick}
        className={newClassName}
      />
    );
  }

  const { children, to, ...rest } = props;
  let isRemarkableDomain = false;
  const isExternal = to.startsWith('http');
  if (isExternal) {
    try {
      isRemarkableDomain = new URL(to).hostname.endsWith('remarkable.com');
    } catch {
      console.error('Invalid URL: ', to);
      // to might not be a valid URL, ignore and keep isRemarkableDomain false
    }
  }

  const addNoReferrerOpener = !isRemarkableDomain && isExternal;

  let externalLinkProps: Partial<ButtonProps> = {
    target: isExternal ? '_blank' : '',
    rel: addNoReferrerOpener ? 'noreferrer noopener' : 'noopener',
  };

  if (isExternal) {
    externalLinkProps = {
      ...externalLinkProps,
      href: to,
      as: 'a',
    };
  } else {
    externalLinkProps = {
      ...externalLinkProps,
      to: to,
      as: RouterLink as unknown as 'a',
    };
  }

  // ArkButton props doesn't infer entirely correct regarding the `as` prop, so we have to cast it to unknown
  return (
    <ArkButton
      ref={ref as unknown}
      {...rest}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(externalLinkProps as any)}
      onClick={onClick}
      aria-disabled={
        (props as ButtonProps).disabled || (props as ButtonProps).loading
      }
      className={newClassName}
    >
      {children}
    </ArkButton>
  );
}
